import type { ResponseProps } from 'store/api/api.types';
import { apiSlice } from 'store/api/apiSlice';
import { fetchPaginatedData } from 'utils/services';

import type {
  ApiEnvelope,
  DetachShopPageSyncPayload,
  MutateShopPagePayload,
  ResolveShopPageSyncPayload,
  ShopComponentRegistryEntry,
  ShopContentfulAsset,
  ShopHomeRouting,
  ShopPage,
  ShopPageDocument,
  ShopPageListInfo,
  ShopPageStatus,
  ShopPageSummary,
  SyncShopPagePayload,
  UpdateShopHomeRoutingPayload,
  UpdateShopPagePayload
} from './shopPages.types';
import type { ShopPageApiShape } from './shopPages.utils';
import {
  isCrossClientShopComponent,
  normalizeShopPage,
  normalizeShopPageSummary,
  serializeShopPageDocument
} from './shopPages.utils';

interface GetShopPagesParams {
  limit?: number;
  pageNo?: number;
  status?: ShopPageStatus;
}

const shopPagesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createShopPage: builder.mutation<ShopPage, ShopPageDocument>({
      invalidatesTags: ['ShopPage'],
      query: (document) => ({
        body: { document: serializeShopPageDocument(document) },
        method: 'POST',
        url: '/admin/shop/pages'
      }),
      transformResponse: (response: ApiEnvelope<ShopPage>) => normalizeShopPage(response.data)
    }),
    deleteShopPage: builder.mutation<ShopPage, MutateShopPagePayload>({
      invalidatesTags: ['ShopPage'],
      query: ({ id, revision }) => ({
        method: 'DELETE',
        params: { revision },
        url: `/admin/shop/pages/${id}`
      }),
      transformResponse: (response: ApiEnvelope<ShopPage>) => normalizeShopPage(response.data)
    }),
    detachShopPageSync: builder.mutation<ShopPage, DetachShopPageSyncPayload>({
      invalidatesTags: (_result, _error, { id }) => ['ShopPage', { id, type: 'ShopPage' }],
      query: ({ contentfulVersion, id, revision }) => ({
        body: { contentfulVersion, revision },
        method: 'POST',
        url: `/admin/shop/pages/${id}/sync/detach`
      }),
      transformResponse: (response: ApiEnvelope<ShopPage>) => normalizeShopPage(response.data)
    }),
    getShopHomeRouting: builder.query<ShopHomeRouting, void>({
      providesTags: ['ShopRouting'],
      query: () => '/admin/shop/routing',
      transformResponse: (response: ApiEnvelope<ShopHomeRouting>) => response.data
    }),
    getShopPage: builder.query<ShopPage, string>({
      providesTags: (_result, _error, id) => [{ id, type: 'ShopPage' }],
      query: (id) => `/admin/shop/pages/${id}`,
      transformResponse: (response: ApiEnvelope<ShopPage>) => normalizeShopPage(response.data)
    }),
    getShopPageComponents: builder.query<ShopComponentRegistryEntry[], void>({
      query: () => '/admin/shop/pages/meta/components',
      transformResponse: (response: ApiEnvelope<ShopComponentRegistryEntry[]>) =>
        response.data.filter(isCrossClientShopComponent)
    }),
    getShopPages: builder.query<
      { data: ShopPageSummary[]; info: ShopPageListInfo },
      GetShopPagesParams | void
    >({
      providesTags: (result) =>
        result
          ? ['ShopPage', ...result.data.map(({ _id }) => ({ id: _id, type: 'ShopPage' as const }))]
          : ['ShopPage'],
      // The admin list renders every page at once, so follow `info` across
      // pages instead of silently truncating at the first `limit` rows.
      async queryFn(arg, api, extraOptions, baseQuery) {
        const { limit = 100, pageNo = 0, status } = arg ?? {};
        const result = await fetchPaginatedData<
          ShopPageApiShape,
          ResponseProps<ShopPageApiShape[], ShopPageListInfo>
        >({
          api,
          baseQuery,
          createEmptyResponse: () => ({
            data: [],
            errorCode: 0,
            info: { nextPageAvailable: false, remainingCount: 0, totalCount: 0 },
            message: ''
          }),
          extraOptions,
          limit,
          pageNo,
          params: status ? { status } : undefined,
          shouldFetchAllPages: true,
          url: '/admin/shop/pages'
        });

        if ('error' in result) return { error: result.error };

        return {
          data: {
            data: result.data.data.map(normalizeShopPageSummary),
            info: { ...result.data.info, nextPageAvailable: false, remainingCount: 0 }
          }
        };
      }
    }),
    publishShopHomeRouting: builder.mutation<ShopHomeRouting, { revision: number }>({
      invalidatesTags: ['ShopRouting'],
      query: ({ revision }) => ({
        body: { revision },
        method: 'POST',
        url: '/admin/shop/routing/publish'
      }),
      transformResponse: (response: ApiEnvelope<ShopHomeRouting>) => response.data
    }),
    publishShopPage: builder.mutation<ShopPage, MutateShopPagePayload>({
      invalidatesTags: ['ShopPage'],
      query: ({ id, revision }) => ({
        body: { revision },
        method: 'POST',
        url: `/admin/shop/pages/${id}/publish`
      }),
      transformResponse: (response: ApiEnvelope<ShopPage>) => normalizeShopPage(response.data)
    }),
    pullShopHomeRouting: builder.mutation<ShopHomeRouting, { revision: number }>({
      invalidatesTags: ['ShopRouting'],
      query: ({ revision }) => ({
        body: { revision },
        method: 'POST',
        url: '/admin/shop/routing/sync/pull'
      }),
      transformResponse: (response: ApiEnvelope<ShopHomeRouting>) => response.data
    }),
    refreshShopPageConflict: builder.mutation<ShopPage, SyncShopPagePayload>({
      invalidatesTags: (_result, _error, { id }) => ['ShopPage', { id, type: 'ShopPage' }],
      query: ({ id, revision }) => ({
        body: { revision },
        method: 'POST',
        url: `/admin/shop/pages/${id}/sync/refresh-conflict`
      }),
      transformResponse: (response: ApiEnvelope<ShopPage>) => normalizeShopPage(response.data)
    }),
    refreshShopPageFromContentful: builder.mutation<ShopPage, SyncShopPagePayload>({
      invalidatesTags: (_result, _error, { id }) => ['ShopPage', { id, type: 'ShopPage' }],
      query: ({ id, revision }) => ({
        body: { revision },
        method: 'POST',
        url: `/admin/shop/pages/${id}/sync/pull`
      }),
      transformResponse: (response: ApiEnvelope<ShopPage>) => normalizeShopPage(response.data)
    }),
    resolveShopPageSync: builder.mutation<ShopPage, ResolveShopPageSyncPayload>({
      invalidatesTags: (_result, _error, { id }) => ['ShopPage', { id, type: 'ShopPage' }],
      query: ({ contentfulVersion, id, resolution, revision }) => ({
        body: { contentfulVersion, resolution, revision },
        method: 'POST',
        url: `/admin/shop/pages/${id}/sync/resolve`
      }),
      transformResponse: (response: ApiEnvelope<ShopPage>) => normalizeShopPage(response.data)
    }),
    restoreDeletedShopPage: builder.mutation<
      { page: ShopPage; restoredAsDraft: boolean },
      MutateShopPagePayload
    >({
      invalidatesTags: ['ShopPage'],
      query: ({ id, revision }) => ({
        body: { revision },
        method: 'POST',
        url: `/admin/shop/pages/${id}/restore`
      }),
      // info.restoredAsDraft distinguishes a validation downgrade from a page
      // that was legitimately a draft when it was deleted.
      transformResponse: (response: ApiEnvelope<ShopPage, { restoredAsDraft?: boolean }>) => ({
        page: normalizeShopPage(response.data),
        restoredAsDraft: Boolean(response.info?.restoredAsDraft)
      })
    }),
    retryShopHomeRoutingSync: builder.mutation<ShopHomeRouting, { revision: number }>({
      invalidatesTags: ['ShopRouting'],
      query: ({ revision }) => ({
        body: { revision },
        method: 'POST',
        url: '/admin/shop/routing/sync/retry'
      }),
      transformResponse: (response: ApiEnvelope<ShopHomeRouting>) => response.data
    }),
    retryShopPageSync: builder.mutation<ShopPage, SyncShopPagePayload>({
      invalidatesTags: (_result, _error, { id }) => ['ShopPage', { id, type: 'ShopPage' }],
      query: ({ id, revision }) => ({
        body: { revision },
        method: 'POST',
        url: `/admin/shop/pages/${id}/sync/retry`
      }),
      transformResponse: (response: ApiEnvelope<ShopPage>) => normalizeShopPage(response.data)
    }),
    unpublishShopHomeRouting: builder.mutation<ShopHomeRouting, { revision: number }>({
      invalidatesTags: ['ShopRouting'],
      query: ({ revision }) => ({
        body: { revision },
        method: 'POST',
        url: '/admin/shop/routing/unpublish'
      }),
      transformResponse: (response: ApiEnvelope<ShopHomeRouting>) => response.data
    }),
    unpublishShopPage: builder.mutation<ShopPage, MutateShopPagePayload>({
      invalidatesTags: ['ShopPage'],
      query: ({ id, revision }) => ({
        body: { revision },
        method: 'POST',
        url: `/admin/shop/pages/${id}/unpublish`
      }),
      transformResponse: (response: ApiEnvelope<ShopPage>) => normalizeShopPage(response.data)
    }),
    updateShopHomeRouting: builder.mutation<ShopHomeRouting, UpdateShopHomeRoutingPayload>({
      invalidatesTags: ['ShopRouting'],
      query: ({ document, revision }) => ({
        body: { document, revision },
        method: 'PATCH',
        url: '/admin/shop/routing'
      }),
      transformResponse: (response: ApiEnvelope<ShopHomeRouting>) => response.data
    }),
    updateShopPage: builder.mutation<ShopPage, UpdateShopPagePayload>({
      invalidatesTags: ['ShopPage'],
      query: ({ document, id, revision }) => ({
        body: { document: serializeShopPageDocument(document), revision },
        method: 'PATCH',
        url: `/admin/shop/pages/${id}`
      }),
      transformResponse: (response: ApiEnvelope<ShopPage>) => normalizeShopPage(response.data)
    }),
    uploadShopAsset: builder.mutation<ShopContentfulAsset, File>({
      query: (file) => {
        const body = new FormData();
        body.append('shopImage', file);
        return { body, method: 'POST', url: '/admin/shop/assets' };
      },
      transformResponse: (response: ApiEnvelope<ShopContentfulAsset>) => response.data
    })
  })
});

export const {
  useCreateShopPageMutation,
  useDeleteShopPageMutation,
  useDetachShopPageSyncMutation,
  useGetShopHomeRoutingQuery,
  useGetShopPageComponentsQuery,
  useGetShopPageQuery,
  useGetShopPagesQuery,
  usePublishShopHomeRoutingMutation,
  usePublishShopPageMutation,
  usePullShopHomeRoutingMutation,
  useRefreshShopPageConflictMutation,
  useRefreshShopPageFromContentfulMutation,
  useResolveShopPageSyncMutation,
  useRestoreDeletedShopPageMutation,
  useRetryShopHomeRoutingSyncMutation,
  useRetryShopPageSyncMutation,
  useUnpublishShopHomeRoutingMutation,
  useUnpublishShopPageMutation,
  useUpdateShopHomeRoutingMutation,
  useUpdateShopPageMutation,
  useUploadShopAssetMutation
} = shopPagesApi;

export { shopPagesApi };
